import React, { useEffect, useRef, useState } from 'react';
import { app } from '../../firebase.js';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import html2pdf from 'html2pdf.js';
import { BRAND_LOGO_ALT, ORGANIZATION_NAME, brandLogo } from '../../branding';
import './financeDocuments.css';

const formatDate = (value) => {
  if (!value) return '--';
  const rawDate = typeof value.toDate === 'function' ? value.toDate() : new Date(value);

  return new Intl.DateTimeFormat('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(rawDate);
};

const formatCurrencyAmount = (value, currency = 'KES') => {
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';
  const numericAmount = Number(value || 0);

  if (!Number.isFinite(numericAmount)) {
    return `${safeCurrency} 0`;
  }

  return `${safeCurrency} ${numericAmount.toLocaleString(safeCurrency === 'USD' ? 'en-US' : 'en-KE', {
    minimumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
    maximumFractionDigits: safeCurrency === 'USD' ? 2 : 0,
  })}`;
};

const resolveReceiptDisplay = (receiptData = {}) => {
  const currency = receiptData.displayCurrency || receiptData.paymentCurrency || receiptData.currency || 'KES';
  const safeCurrency = currency === 'USD' ? 'USD' : 'KES';

  if (safeCurrency === 'USD') {
    return {
      amount: Number(receiptData.displayAmount || receiptData.incomeAmount || receiptData.amount || 0),
      currency: safeCurrency,
    };
  }

  return {
    amount: Number(receiptData.amountInKes || receiptData.incomeAmount || receiptData.amount || 0),
    currency: safeCurrency,
  };
};

function ViewReceipt() {
  const [receiptData, setReceiptData] = useState(null);
  const [studentName, setStudentName] = useState('');
  const [admissionNumber, setAdmissionNumber] = useState('');
  const [studentProfile, setStudentProfile] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [qrUnavailable, setQrUnavailable] = useState(false);
  const receiptRef = useRef(null);

  const receivedId = decodeURIComponent(window.location.search);
  const docID = parseInt(receivedId.substring(1), 10);
  const db = getFirestore(app);
  const receiptUrl = window.location.href;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(receiptUrl)}`;

  useEffect(() => {
    const fetchReceipt = async () => {
      setIsLoading(true);
      setLoadError('');
      setQrUnavailable(false);

      if (Number.isNaN(docID)) {
        setLoadError('The receipt link is invalid.');
        setIsLoading(false);
        return;
      }

      try {
        const receiptQuery = query(collection(db, 'Income'), where('invoiceNumber', '==', docID));
        const receiptSnapshot = await getDocs(receiptQuery);

        if (receiptSnapshot.empty) {
          setLoadError('Receipt not found.');
          return;
        }

        const data = receiptSnapshot.docs[0].data();
        setReceiptData(data);

        const userQuery = query(collection(db, 'users'), where('userId', '==', data.studentId));
        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userData = userSnapshot.docs[0].data();
          setStudentName(userData.bio || '');
          setAdmissionNumber(userData.admissionNumber || '');
          setStudentProfile(userData);
          return;
        }

        setStudentName(data.studentName || '');
        setAdmissionNumber(data.admissionNumber || '');
      } catch (error) {
        console.error('Error fetching receipt:', error);
        setLoadError('We could not load this receipt right now.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReceipt();
  }, [db, docID]);

  const exportPDF = () => {
    if (!receiptRef.current) return;

    html2pdf()
      .from(receiptRef.current)
      .set({
        margin: 10,
        filename: `${docID}_receipt.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .save();
  };

  if (isLoading) {
    return (
      <div className="financeDocumentPage">
        <div className="financeDocumentStateCard">
          <p className="financeDocumentStateEyebrow">Payment receipt</p>
          <h2>Loading receipt...</h2>
          <p className="mb-0">Please wait while we prepare the payment details.</p>
        </div>
      </div>
    );
  }

  if (loadError || !receiptData) {
    return (
      <div className="financeDocumentPage">
        <div className="financeDocumentStateCard">
          <p className="financeDocumentStateEyebrow">Payment receipt</p>
          <h2>Receipt unavailable</h2>
          <p className="mb-0">{loadError || 'We could not find the requested receipt.'}</p>
        </div>
      </div>
    );
  }

  const paymentDisplay = resolveReceiptDisplay(receiptData);
  const formattedPaidAmount = formatCurrencyAmount(paymentDisplay.amount, paymentDisplay.currency);
  const courseLabel = receiptData.courseName || receiptData.course || studentProfile.course || 'Fee payment';

  return (
    <div className="financeDocumentPage">
      <div className="financeDocumentShell">
        <div ref={receiptRef} className="financeDocumentCard">
          <div className="financeDocumentHeader">
            <div className="financeDocumentBrand">
              <img src={brandLogo} className="financeDocumentBrandLogo" alt={BRAND_LOGO_ALT} />
              <div>
                <p className="financeDocumentEyebrow">{ORGANIZATION_NAME}</p>
                <h1>Payment Receipt</h1>
                <p className="financeDocumentMuted mb-0">Official confirmation of fee payment</p>
              </div>
            </div>
            <div className="financeDocumentHeaderMeta">
              <div className="financeDocumentMetaStack">
                <span className="financeDocumentMetaLabel">Receipt No.</span>
                <strong>{receiptData.invoiceNumber}</strong>
              </div>
              <div className="financeDocumentMetaStack">
                <span className="financeDocumentMetaLabel">Date</span>
                <strong>{formatDate(receiptData.paymentDate || receiptData.timeCreated)}</strong>
              </div>
            </div>
          </div>

          <div className="financeDocumentDivider" />

          <div className="financeDocumentTopGrid">
            <div className="financeDocumentPartyBlock">
              <p className="financeDocumentSectionEyebrow">Received from</p>
              <h2>{studentName || 'Student record unavailable'}</h2>
              <p className="financeDocumentPartyLine">Admission No. {admissionNumber || '--'}</p>
              <div className="financeDocumentMetaList">
                <div className="financeDocumentMetaListRow">
                  <span>Payment method</span>
                  <strong>{receiptData.paymentMethod || 'Not provided'}</strong>
                </div>
                <div className="financeDocumentMetaListRow">
                  <span>Programme</span>
                  <strong>{courseLabel}</strong>
                </div>
                <div className="financeDocumentMetaListRow">
                  <span>Status</span>
                  <strong>Paid in full</strong>
                </div>
              </div>
            </div>

            <div className="financeDocumentQrPanel">
              <p className="financeDocumentSectionEyebrow">Verification</p>
              <div className="financeDocumentQrFrame">
                {qrUnavailable ? (
                  <div className="financeDocumentQrFallback">
                    <span>Verification link</span>
                    <strong>{receiptData.invoiceNumber}</strong>
                  </div>
                ) : (
                  <img
                    src={qrCodeUrl}
                    alt={`QR code for receipt ${receiptData.invoiceNumber}`}
                    className="financeDocumentQrImage"
                    crossOrigin="anonymous"
                    onError={() => setQrUnavailable(true)}
                  />
                )}
              </div>
              <p className="financeDocumentQrCaption mb-0">Scan to open this receipt online.</p>
            </div>
          </div>

          <div className="financeDocumentSnapshotGrid">
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Amount paid</span>
              <strong>{formattedPaidAmount}</strong>
            </div>
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Payment date</span>
              <strong>{formatDate(receiptData.paymentDate || receiptData.timeCreated)}</strong>
            </div>
            <div className="financeDocumentSnapshotItem">
              <span className="financeDocumentMetaLabel">Receipt reference</span>
              <strong>{receiptData.invoiceNumber}</strong>
            </div>
          </div>

          <div className="financeDocumentTableWrap">
            <table className="table financeDocumentTable mb-0">
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Payment Method</th>
                  <th className="text-end">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="Description">{courseLabel}</td>
                  <td data-label="Payment Method">{receiptData.paymentMethod || 'Not provided'}</td>
                  <td data-label="Amount" className="text-end">{formattedPaidAmount}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="financeDocumentTotalBar">
            <span>Total paid</span>
            <strong>{formattedPaidAmount}</strong>
          </div>

          <div className="financeDocumentFooter">
            <div className="financeDocumentFooterMeta">
              <span>This receipt is computer-generated and does not require a signature.</span>
              <strong>Reference: {receiptData.invoiceNumber}</strong>
            </div>
            <p className="mb-0">Keep this receipt for your records and future fee reconciliation.</p>
          </div>
        </div>

        <div className="financeDocumentActions">
          <button onClick={exportPDF} className="financeDocumentDownloadBtn">
            Download as PDF
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewReceipt;
